import { Auth } from '@spike/auth-model';
import { 
    OrganizationsLoginResponseDto, 
    OrganizationsUserDto,
    OrganizationDto,
    OrgAccessLevelNames 
} from './OrganizationsLoginDtos';

/**
 * Convert Organizations Login Response to internal format
 * Returns the original response with proper typing
 */
export const convertOrganizationsLogin = (
    response: OrganizationsLoginResponseDto
): OrganizationsLoginResponseDto => {
    // Simply return the response as-is, properly typed
    // The Auth object will be created in the actions layer
    return response;
};

/**
 * Convert Organizations User DTO to Auth format
 * Creates the proper Auth structure with nested user object
 */
export const convertOrganizationsUser = (userDto: OrganizationsUserDto): Auth => ({
    user: {
        id: userDto.id,
        firstName: userDto.first_name,
        lastName: userDto.last_name,
        avatar: userDto.image || '', // Use Gravatar image or empty
        email: userDto.email,
        staffId: userDto.staff_id || 0, // Use staff_id from backend or 0
        admin: userDto.admin,
        role: {
            id: userDto.org_access_level_id,
            name: OrgAccessLevelNames[userDto.org_access_level_id]
        },
        accessLevel: {
            id: userDto.org_access_level_id,
            name: OrgAccessLevelNames[userDto.org_access_level_id]
        }
    },
    signedIn: true,
    token: userDto.token,
    marketplaceId: userDto.marketplace_id || 0, // Use marketplace_id from backend (null becomes 0)
    addressId: 0,
    timeZone: '',
    marketplaces: []
});

/**
 * Get user display name from OrganizationsUserDto
 */
export const getUserDisplayName = (user: OrganizationsUserDto): string => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName} ${lastName}`.trim() || user.email;
};

/**
 * Get user display name from Auth object
 */
export const getAuthUserDisplayName = (auth: Auth): string => {
    if (!auth.user) return '';
    const firstName = auth.user.firstName || '';
    const lastName = auth.user.lastName || '';
    return `${firstName} ${lastName}`.trim() || auth.user.email;
};

/**
 * Check if user has admin access (from OrganizationsUserDto)
 */
export const isAdminUser = (user: OrganizationsUserDto): boolean => {
    return user.admin || user.org_access_level_id === 1;
};

/**
 * Check if auth user has admin access
 */
export const isAuthAdminUser = (auth: Auth): boolean => {
    return auth.user?.admin || auth.user?.accessLevel?.id === 1;
};

/**
 * Check if user has manager access or higher (from OrganizationsUserDto)
 */
export const isManagerUser = (user: OrganizationsUserDto): boolean => {
    return user.org_access_level_id <= 2;
};

/**
 * Check if auth user has manager access or higher
 */
export const isAuthManagerUser = (auth: Auth): boolean => {
    return (auth.user?.accessLevel?.id || 3) <= 2;
};

/**
 * Get access level display name
 */
export const getAccessLevelName = (level: number): string => {
    return OrgAccessLevelNames[level as keyof typeof OrgAccessLevelNames] || 'Unknown';
};

